/**
 * ML Context — browser-side feature capture for activity enrichment.
 *
 * Produces a lightweight snapshot that gets merged into every logAction()
 * call under `metadata.ml_context`. The backend enriches it further with
 * server-side features (response_time, lead snapshot, advisor load, etc.).
 *
 * Session ID persists per browser tab (sessionStorage) so we can group
 * interactions within a single working session.
 */

const SESSION_KEY = 'owly_ml_session_id';

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function getDeviceType(width: number): 'mobile' | 'tablet' | 'desktop' {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

export interface MLContext {
  device_type: 'mobile' | 'tablet' | 'desktop';
  screen_width: number;
  screen_height: number;
  pixel_ratio: number;
  platform: string;
  timezone: string;
  locale: string;
  session_id: string;
  page_path: string;
  interaction_timestamp_ms: number;
  online: boolean;
  connection_type: string | null;
}

export function buildMLContext(): MLContext {
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string };
  };

  return {
    device_type: getDeviceType(window.innerWidth),
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    pixel_ratio: window.devicePixelRatio || 1,
    platform: navigator.platform,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
    session_id: getSessionId(),
    page_path: window.location.pathname,
    interaction_timestamp_ms: Date.now(),
    online: navigator.onLine,
    connection_type: nav.connection?.effectiveType ?? null,
  };
}
